import { lighten } from "polished"
import { mediaQuery } from "shared/styles/mixins"
import styled from "styled-components"

/* =========================================
LAYOUT
============================================ */

export const Form = styled.form`
  width: 100%;
  line-height: 1.5;
`

// For checkout form pages that do not contain an actual form.
export const FormWrapper = styled.div`
  padding: 10px 0px;
  width: 100%;
  line-height: 1.5;
`

export const CheckoutMessage = styled.div`
  margin: 30px auto;
  padding: 10px;
  width: 80%;

  p {
    text-align: center;
  }
`

export const Fieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  line-height: normal;

  &:not(last-child) {
    margin-bottom: 15px;
  }
`

export const InputGroup = styled.div`
  display: grid;
  grid-gap: 15px;
  grid-template-columns: 1fr;
  padding: 20px 0px 10px;
`

// Inputs on Product Selection page do not exist in regular Checkout form layout
export const ProductSelectionInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 0 20px;

  & > div {
    margin-top: 15px;
  }
`

export const RadioGroup = styled.div`
  margin-top: 20px;
  padding: 0 10px;
`

export const FormInput = styled.div`
  grid-column: span 1;
`

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 20px 0 15px;
`

/* =========================================
TEXT STYLES
============================================ */

export const Legend = styled.legend`
  color: ${(props) => props.theme.text.dark};
  font-size: 1.14rem;
  font-weight: 700;
`

export const InputError = styled.p`
  margin: 2px 2px 0;
  color: ${(props) => props.theme.colors.error};
  font-size: ${(props) => props.theme.input.errorText};
`

export const InnerLabel = styled.label<{ $showLabel?: boolean }>`
  position: absolute;
  top: ${(props) => (props.$showLabel ? "5px" : "-20px")};
  color: ${(props) => props.theme.input.labelColor};
  font-size: ${(props) => props.theme.input.labelSize};
  user-select: none;
  transition: top 100ms ease-in;
  pointer-events: none;
`

export const RadioLabel = styled.label<{ $disabled?: boolean }>`
  font-size: ${(props) => props.theme.input.textSize};
  color: ${(props) =>
    props.$disabled ? props.theme.text.light : props.theme.text.dark};

  & * {
    color: ${(props) =>
      props.$disabled
        ? props.theme.text.light
        : props.theme.text.dark} !important;
  }
`

/* =========================================
INPUTS
============================================ */

export const InputWrapper = styled.div<{ $error?: any; height?: number }>`
  width: 100%;
  line-height: 1;
  margin: 0;
  border-width: ${(props) => props.theme.input.borderWidth};
  border-style: solid;
  border-color: ${({ theme, $error: error }) =>
    error ? theme.colors.error : theme.input.borderColor};
  font-size: ${(props) => props.theme.input.textSize};
  padding: 8px;
  border-radius: ${(props) => props.theme.input.borderRadius};
  height: ${({ theme, height }) =>
    height ? `${height}px` : theme.input.height};
  overflow: hidden;
  position: relative;

  &:focus-within {
    border-color: ${(props) => props.theme.input.borderFocus};
  }

  ${mediaQuery.mobileDevice} {
    height: ${(props) => props.theme.mobile.input.height};
  }
`

export const Input = styled.input<{ $showLabel: boolean; type: string }>`
  background-color: transparent;
  width: 100%;
  border: none;
  font-size: ${(props) => props.theme.input.textSize};
  margin-top: ${(props) => (props.$showLabel ? "9px" : "3px")};
  transition: all 100ms ease-in;
  color: ${(props) => props.theme.input.textColor};

  &::placeholder {
    color: ${(props) => props.theme.input.placeholder};
    opacity: 1;
  }

  &:focus,
  &:active {
    outline: none;
  }

  &:invalid {
    outline: none;
    box-shadow: none;
  }
`

export const TextArea = styled.textarea<{ $showLabel: boolean }>`
  background-color: transparent;
  width: 100%;
  height: 100px;
  border: none;
  font-family: ${(props) => props.theme.font.primary};
  font-size: ${(props) => props.theme.input.textSize};
  font-weight: 500;
  margin-top: ${(props) => (props.$showLabel ? "9px" : "3px")};
  transition: all 100ms ease-in;
  color: ${(props) => props.theme.input.textColor};
  resize: none;

  &::placeholder {
    color: ${(props) => props.theme.input.placeholder};
    opacity: 1;
  }

  &:focus,
  &:active {
    outline: none;
  }

  &:invalid {
    outline: none;
    box-shadow: none;
  }
`

export const SelectWrapper = styled.div<{ $showLabel?: boolean }>`
  background-color: ${(props) => props.theme.input.background};
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;

  select {
    background-color: ${(props) => props.theme.input.background};
    appearance: none;
    width: 100%;
    height: 100%;
    border: none;
    font-size: ${(props) => props.theme.input.textSize};
    padding-left: 10px;
    padding-top: ${(props) => (props.$showLabel ? "13px" : "0")};
    transition: all 100ms ease-in;
    color: ${(props) => props.theme.input.textColor};
    cursor: pointer;

    &:-moz-focusring {
      color: transparent;
      text-shadow: 0 0 0 #000;
    }

    &:focus,
    &:active {
      outline: none;
    }

    option {
      color: ${(props) => props.theme.input.textColor};

      &.placeholder {
        color: ${(props) => props.theme.input.disabled};
      }
    }
  }
`

export const OpenSelect = styled.div`
  padding: 5px;
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.input.textColor};
  pointer-events: none;
  font-size: 14px;

  ${mediaQuery.mobileDevice} {
    top: 10px;
    right: 10px;
  }
`

export const RadioButton = styled.input.attrs({ type: "radio" })`
  appearance: none;
  border: 1px solid ${(props) => props.theme.input.borderColor};
  height: 18px;
  width: 18px;
  background-color: transparent;
  border-radius: 50%;
  margin: 0;
  margin-right: 10px;
  transition: box-shadow 150ms ease-in;
  box-shadow: ${(
    props,
  ) => `${props.theme.input.background} 0px 0px 0px 10px inset,
    ${props.theme.button.bgColor} 0px 0px 0px 10px inset`};

  &:checked {
    border-color: ${(props) => props.theme.button.bgColor};
    box-shadow: ${(
      props,
    ) => `${props.theme.input.background} 0px 0px 0px 4px inset,
    ${props.theme.button.bgColor} 0px 0px 0px 10px inset`};
  }

  &:checked:disabled {
    border-color: ${(props) => props.theme.input.borderDisabled};
    box-shadow: ${(
      props,
    ) => `${props.theme.input.background} 0px 0px 0px 4px inset,
    ${props.theme.input.borderDisabled} 0px 0px 0px 10px inset`};
  }

  &:focus-visible:not(:checked) {
    outline: none !important;
    box-shadow: 0 0 0 3px
      ${(props) => lighten(0.2, props.theme.button.bgColor)};
  }

  &:focus-visible:checked {
    outline: none !important;
    box-shadow: 0 0 0 3px
      ${(props) => lighten(0.2, props.theme.button.bgColor)};
    box-shadow: ${(props) => `${
      props.theme.input.background
    } 0px 0px 0px 4px inset,
    ${props.theme.button.bgColor} 0px 0px 0px 10px inset,
    0 0 0 3px ${lighten(0.2, props.theme.button.bgColor)}`};
  }
`

export const RadioOption = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`
